// @ts-ignore
import {MarkerClusterer} from "@googlemaps/markerclusterer";

type AdvancedMarkerElement = google.maps.marker.AdvancedMarkerElement;

class UrgentCareMap {
    private readonly map: google.maps.Map;
    private readonly infoWindow = new google.maps.InfoWindow();
    private clusterer: MarkerClusterer | null = null;
    private readonly markers: AdvancedMarkerElement[] = [];

    constructor(private readonly mapElement: HTMLElement) {
        this.map = new google.maps.Map(mapElement, {
            maxZoom: 17,
            mapId: mapElement.getAttribute('data-map-id'),
            zoomControl: true,
            scaleControl: true,
            mapTypeControl: false,
            streetViewControl: false
        });
        
        // Small delay to ensure DOM is ready
        setTimeout(() => this.setupMarkers(), 100);
    }

    private async setupMarkers() {
        try {
            const {AdvancedMarkerElement} = await google.maps.importLibrary('marker') as google.maps.MarkerLibrary;
            
            // Fetch and prepare the SVG icon
            const basePin = await fetch(new URL('/wp-content/themes/plaudit-theme-child/blocks/UrgentCareLocationsMap/map-marker.svg', document.baseURI))
                .then(icon => {
                    if (!icon.ok) {
                        throw new Error("Unable to download the map marker");
                    }
                    return icon.text();
                })
                .then(icon => new DOMParser().parseFromString(icon, 'image/svg+xml').documentElement);

            // Fix the SVG - ensure the path has a visible fill
            const path = basePin.querySelector('path');
            if (path && (!path.getAttribute('fill') || path.getAttribute('fill') === 'none')) {
                path.setAttribute('fill', '#7923b5');
            }

            // Remove the transparent rectangle that might interfere
            const transparentRect = basePin.querySelector('rect[opacity="0"]');
            if (transparentRect) {
                transparentRect.remove();
            }

            const bounds = new google.maps.LatLngBounds();
            const locations = this.mapElement.querySelectorAll<HTMLElement>('[data-lat][data-lng]');
            
            // Check if global data is available as fallback
            const globalLocations = (window as any).urgentCareLocations;
            
            if (locations.length === 0 && globalLocations && Array.isArray(globalLocations)) {
                // Use global location data as fallback
                globalLocations.forEach((location: any, index: number) => {
                    const position = {
                        lat: location.lat,
                        lng: location.lng
                    };

                    const icon = basePin.cloneNode(true) as Element;
                    icon.setAttribute('data-type', 'urgent-care');

                    const marker = new AdvancedMarkerElement({
                        position,
                        content: icon,
                        map: this.map,
                        title: location.title
                    });

                    marker.addListener('click', () => {
                        this.infoWindow.setContent(`<div><h3>${location.title}</h3><p>${location.address || ''}</p></div>`);
                        this.infoWindow.open(this.map, marker);
                    });

                    bounds.extend(position);
                    this.markers.push(marker);
                });
            } else {
                // Use DOM elements
                locations.forEach((element, index) => {
                    const {lat, lng} = element.dataset;
                    if (!lat || !lng) {
                        console.warn(`Location ${index} missing lat/lng data`);
                        return;
                    }

                    const position = {
                        lat: parseFloat(lat),
                        lng: parseFloat(lng)
                    };

                    const icon = basePin.cloneNode(true) as Element;
                    icon.setAttribute('data-type', 'urgent-care');

                    const marker = new AdvancedMarkerElement({
                        position,
                        content: icon,
                        map: this.map,
                        title: `Urgent Care Location ${index + 1}`
                    });

                    marker.addListener('click', () => {
                        const content = element.cloneNode(true) as HTMLElement;
                        content.style.display = 'block';
                        this.infoWindow.setContent(content);
                        this.infoWindow.open(this.map, marker);
                    });

                    bounds.extend(position);
                    this.markers.push(marker);
                });
            }
            
            // Create clusterer instance
            if (this.clusterer) {
                this.clusterer.setMap(null);
            }
            
            this.clusterer = new MarkerClusterer({
                map: this.map,
                markers: this.markers as any,
                onClusterClick: (_event: google.maps.MapMouseEvent, cluster: any) => {
                    this.infoWindow.close();
                    if (cluster?.markers?.length) {
                        const clusterBounds = new google.maps.LatLngBounds();
                        cluster.markers.forEach((marker: any) => {
                            if (marker.position) {
                                clusterBounds.extend(marker.position);
                            }
                        });
                        this.map.fitBounds(clusterBounds, 20);
                    }
                }
            });
            
            if (this.markers.length > 0) {
                this.map.fitBounds(bounds, 20);
            } else {
                console.warn('No markers were created. Check that location elements have valid lat/lng data.');
                
                // If no markers, at least center the map somewhere
                this.map.setCenter({lat: 44.9778, lng: -93.2650}); // Minneapolis
                this.map.setZoom(10);
            }
            
        } catch (error) {
            console.error('Error setting up SVG markers:', error);
        }
    }
}

document.addEventListener('plauditBase__mapInitialized', () => {
    document.querySelectorAll<HTMLElement>('.urgent-care-locations-map').forEach(element => {
        if (!element.classList.contains('urgent-care-locations-map--setup')) {
            element.classList.add('urgent-care-locations-map--setup');
            new UrgentCareMap(element);
        }
    });
}); 
